/**
 * The Live Board: the homepage hero, and a real leaderboard in miniature.
 *
 * Four rows, the actual top four of the global board, at the actual figures
 * their companies have paid. It moves when a payment clears and at no other
 * time — the rows are fed by the live channel, and when that channel is not
 * carrying they are fed by re-reading the same board on a slow timer. Either
 * way the motion is the same, and either way it is a report rather than a
 * performance: nothing here ticks to look busy.
 *
 * The motion language is the approved concept, designs/hero-ideas/board-reshuffle.html:
 *
 *   1. the row lifts off the ruled ground and an indigo wash sweeps across it
 *   2. its figure rolls, odometer-style, to the new total
 *   3. the board reorders, every row gliding on a gentle spring — the moved row
 *      to its new rank, the row it displaced settling into the one it left
 *   4. the lift, the wash, and the rail fade out and the row rejoins the page
 *
 * The rank numerals and the hairlines are a separate, static layer. They never
 * move, so the rows are seen to change places rather than the whole board
 * appearing to scroll.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { MINIMUM_FIRST_BID_CENTS, type RankedEntry } from '@bidtohire/shared';

import { useLiveBids } from '../lib/live';
import {
  BOARD_SIZE,
  applyBid,
  reconcile,
  rowsFromEntries,
  type BoardRow,
} from '../lib/live-board';
import { compactCompensationLabel } from '../lib/format';
import { Money } from './Money';
import { Odometer } from './Odometer';
import { CompanyMark } from './ui';

/*
 * Spring constants, carried over from the prototype unchanged. Semi-implicit
 * Euler with two sub-steps: stiff enough to arrive inside ~600ms, damped
 * enough that the overshoot is a settle rather than a bounce.
 */
const SPRING_K = 168;
const SPRING_C = 25;
const SPRING_EPSILON = 0.12;

/** Fallback row height, used only before the stylesheet has been read. */
const DEFAULT_ROW_HEIGHT = 78;

/* The beats of a bid, in milliseconds after the event arrives. */
const ROLL_AT = 150;
const REORDER_AT = 480;
const SETTLE_AT = 1240;
const EXIT_AFTER = 1400;

interface RowMotion {
  y: number;
  v: number;
  target: number;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The row height the stylesheet is currently using, at this breakpoint. */
function rowHeight(element: HTMLElement | null): number {
  if (element === null || typeof window.getComputedStyle !== 'function') {
    return DEFAULT_ROW_HEIGHT;
  }
  const declared = window.getComputedStyle(element).getPropertyValue('--lb-row');
  const parsed = Number.parseFloat(declared);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_ROW_HEIGHT;
}

export function LiveBoard({
  entries,
  totalListings,
  isLoading,
  isError,
  onStale,
}: {
  readonly entries: readonly RankedEntry[];
  readonly totalListings: number;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** Ask for a fresh read; called when a bid lands that the board cannot see. */
  readonly onStale: () => void;
}): ReactNode {
  const [rows, setRows] = useState<readonly BoardRow[]>(() => rowsFromEntries(entries));
  /** A displaced row, held on screen just long enough to be seen leaving. */
  const [leaving, setLeaving] = useState<readonly BoardRow[]>([]);
  const [highlighted, setHighlighted] = useState<string | undefined>(undefined);

  const slots = useRef<HTMLDivElement | null>(null);
  const nodes = useRef(new Map<string, HTMLElement>());
  const motion = useRef(new Map<string, RowMotion>());
  const frame = useRef<number | undefined>(undefined);
  const timers = useRef<number[]>([]);

  /** What the last read told us, so a re-render does not undo a live update. */
  const readSignature = useRef<string | undefined>(undefined);

  const later = (fn: () => void, delay: number): void => {
    timers.current.push(window.setTimeout(fn, delay));
  };

  // -------------------------------------------------------------------------
  // Data in: a fresh read, and a pushed bid
  // -------------------------------------------------------------------------

  const signature = useMemo(
    () =>
      entries
        .slice(0, BOARD_SIZE)
        .map((entry) => `${entry.jobId}:${entry.totalPaidCents}`)
        .join('|'),
    [entries],
  );

  useEffect(() => {
    // Only a genuinely different read is folded in. Without this guard every
    // re-render of the page would overwrite an optimistic reorder with the
    // cached board it was applied to.
    if (readSignature.current === signature) return;
    readSignature.current = signature;
    setRows((current) => reconcile(current, entries).rows);
  }, [signature, entries]);

  const { isLive } = useLiveBids({
    onBid: (event) => {
      if (!event.scopes.includes('global')) return;

      const result = applyBid(rows, event);

      if (result.outcome === 'unseen') {
        // The bid was real and it happened below the four rows on show. The
        // board holds still for it — but the figures around it, the count and
        // the price of the top spot, have moved and are re-read.
        onStale();
        return;
      }

      const dropped = rows.filter((row) => !result.rows.some((kept) => kept.jobId === row.jobId));

      if (prefersReducedMotion()) {
        setRows(result.rows);
        onStale();
        return;
      }

      // The beats of the concept, in order: the row lifts, its money rolls,
      // the board reorders, and the highlight fades back into the page.
      setHighlighted(event.jobId);
      later(() => setRows(result.staged), ROLL_AT);
      later(() => {
        if (dropped.length > 0) {
          setLeaving(dropped);
          later(() => setLeaving([]), EXIT_AFTER);
        }
        setRows(result.rows);
      }, REORDER_AT);
      later(() => setHighlighted(undefined), SETTLE_AT + 600);
      // The re-read is asked for last. The rows themselves came from the event,
      // but the figures stated around them did not — and a read that landed
      // mid-reshuffle would cut the choreography off at whichever beat it
      // arrived on.
      later(onStale, SETTLE_AT);
    },
    onResume: onStale,
  });

  // -------------------------------------------------------------------------
  // Motion out: the spring that carries every row to its place
  // -------------------------------------------------------------------------

  useLayoutEffect(() => {
    const height = rowHeight(slots.current);
    const instant = prefersReducedMotion();
    const live = new Set<string>();

    rows.forEach((row, index) => {
      live.add(row.jobId);
      const existing = motion.current.get(row.jobId);
      if (existing === undefined) {
        // A row that was not here a moment ago rises into place from just below
        // the board rather than appearing inside it.
        const from = instant ? index * height : rows.length * height;
        motion.current.set(row.jobId, { y: from, v: 0, target: index * height });
      } else {
        existing.target = index * height;
        if (instant) {
          existing.y = existing.target;
          existing.v = 0;
        }
      }
    });

    leaving.forEach((row) => {
      live.add(row.jobId);
      const existing = motion.current.get(row.jobId);
      const target = rows.length * height;
      if (existing === undefined) motion.current.set(row.jobId, { y: target, v: 0, target });
      else existing.target = target;
    });

    for (const jobId of [...motion.current.keys()]) {
      if (!live.has(jobId)) motion.current.delete(jobId);
    }

    const write = (): boolean => {
      let moving = false;
      for (const [jobId, state] of motion.current) {
        const node = nodes.current.get(jobId);
        if (node === undefined) continue;
        node.style.transform = `translateY(${state.y}px)`;
        if (Math.abs(state.target - state.y) > SPRING_EPSILON || Math.abs(state.v) > SPRING_EPSILON) {
          moving = true;
        }
      }
      return moving;
    };

    if (instant || typeof requestAnimationFrame !== 'function') {
      for (const state of motion.current.values()) {
        state.y = state.target;
        state.v = 0;
      }
      write();
      return;
    }

    let last = performance.now();
    const tick = (now: number): void => {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;

      for (const state of motion.current.values()) {
        const distance = state.target - state.y;
        if (Math.abs(distance) <= SPRING_EPSILON && Math.abs(state.v) <= SPRING_EPSILON) {
          state.y = state.target;
          state.v = 0;
          continue;
        }
        // Two sub-steps: one integration at 60Hz is stiff enough to ring.
        const step = dt / 2;
        for (let s = 0; s < 2; s += 1) {
          state.v += (SPRING_K * (state.target - state.y) - SPRING_C * state.v) * step;
          state.y += state.v * step;
        }
      }

      const moving = write();
      frame.current = moving ? requestAnimationFrame(tick) : undefined;
    };

    if (frame.current === undefined) {
      write();
      last = performance.now();
      frame.current = requestAnimationFrame(tick);
    }
  }, [rows, leaving]);

  useEffect(
    () => () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
      for (const timer of timers.current) window.clearTimeout(timer);
      timers.current = [];
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const visible = [...rows, ...leaving];
  const slotCount = Math.max(rows.length, BOARD_SIZE);

  return (
    <section className="liveboard" aria-label="Live leaderboard">
      <div className="lb-head" aria-hidden="true">
        <span />
        <span>Role</span>
        <span className="lb-col-salary">Salary</span>
        <span className="lb-col-amount">Paid to rank here</span>
      </div>

      <div
        className="lb-slots"
        ref={slots}
        style={{ height: `calc(var(--lb-row) * ${slotCount})` }}
      >
        {Array.from({ length: slotCount }, (_unused, index) => (
          <div
            className={index === slotCount - 1 ? 'lb-slot is-last' : 'lb-slot'}
            key={`slot-${index}`}
            style={{ top: `calc(var(--lb-row) * ${index})` }}
            aria-hidden="true"
          >
            <span className="lb-rank mono">{index + 1}</span>
          </div>
        ))}

        {rows.length === 0 && !isLoading ? (
          <div className="lb-empty">
            {isError ? (
              <p>The board could not be read just now.</p>
            ) : (
              <>
                <p>No listings on the board yet.</p>
                <p className="lb-empty-note">
                  The first <Money cents={MINIMUM_FIRST_BID_CENTS} /> puts a role here and takes
                  the top position.
                </p>
                <Link className="pill pill-primary" to="/post">
                  Post the first role
                </Link>
              </>
            )}
          </div>
        ) : null}

        {visible.map((row, index) => {
          const isLeaving = index >= rows.length;
          return (
            <Link
              key={row.jobId}
              to={`/jobs/${row.jobId}`}
              className={[
                'lb-row',
                highlighted === row.jobId ? 'is-moving' : '',
                isLeaving ? 'is-leaving' : '',
              ]
                .filter((part) => part !== '')
                .join(' ')}
              ref={(node) => {
                if (node === null) nodes.current.delete(row.jobId);
                else nodes.current.set(row.jobId, node);
              }}
            >
              <span className="lb-lift" aria-hidden="true" />
              <span className="lb-wash" aria-hidden="true" />
              <span className="lb-rail" aria-hidden="true" />

              <CompanyMark
                company={{
                  name: row.companyName,
                  slug: row.companySlug,
                  ...(row.companyLogoKey === undefined ? {} : { logoKey: row.companyLogoKey }),
                }}
                size="sm"
              />

              <span className="lb-role">
                <b>{row.title}</b>
                <span>{row.companyName}</span>
              </span>

              <span className="lb-salary mono">
                {compactCompensationLabel(row.salaryLabel) ?? 'On application'}
              </span>

              <Odometer cents={row.totalCents} />
            </Link>
          );
        })}
      </div>

      <div className="lb-foot">
        <span className="lb-note">
          {isLoading && rows.length === 0
            ? 'Reading the board…'
            : boardNote(rows.length, totalListings)}
        </span>
        <span className={isLive ? 'lb-live is-on' : 'lb-live'}>
          <span className="lb-dot" aria-hidden="true" />
          {isLive ? 'Live' : 'Updating'}
        </span>
      </div>
    </section>
  );
}

/** "Top 4 of 1,286 open roles" — and the honest thing to say when it is not. */
function boardNote(shown: number, total: number): string {
  if (shown === 0) return 'Nothing on the board yet';
  const counted = Math.max(total, shown);
  if (counted <= shown) return `${shown === 1 ? 'The only role' : `All ${shown} roles`} on the board`;
  return `Top ${shown} of ${counted.toLocaleString('en-US')} open roles`;
}
